import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Calendar, Check, ChefHat, Eye, EyeOff, Loader2, Users, Wand2 } from "lucide-react";
import PhoneInput, { type Country } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MapPicker } from "@/components/maps/MapPicker";
import { usePageMeta } from "@/hooks/use-page-meta";
import { isNativeIosApp } from "@/lib/platform";

type PrimaryModule = "appointments" | "memberships" | "catering_services";

const accountSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters and include at least one number"),
  confirmPassword: z.string(),
  phone: z.string().min(1, "Phone number is required"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine((data) => {
  const parsed = parsePhoneNumberFromString(String(data.phone || ""));
  return !!parsed?.isValid();
}, {
  message: "Please enter a valid phone number.",
  path: ["phone"],
});

const businessSchema = z.object({
  businessName: z.string().min(2, "Business name is required"),
  location: z.string().min(1, "Business location is required"),
  locationPlaceId: z.string().min(1, "Select a location from the suggestions or map"),
  locationCity: z.string().optional(),
  locationCountry: z.string().optional(),
  locationLat: z.number().optional(),
  locationLng: z.number().optional(),
  timezone: z.string().min(1),
  currency: z.string().min(1),
});

type AccountFormData = z.infer<typeof accountSchema>;
type BusinessFormData = z.infer<typeof businessSchema>;

const STEP_COPY = {
  1: {
    label: "Module",
    title: "What kind of business are you running?",
    description: "Start with the setup that best matches how you take bookings, subscriptions, or orders.",
  },
  2: {
    label: "Business",
    title: "Tell us about your business",
    description: "Pick your location and we will automatically set your timezone and currency.",
  },
  3: {
    label: "Account",
    title: "Create your account",
    description: "Add your login details so you can start using Buukd.",
  },
} as const;

const MODULE_OPTIONS: Array<{
  value: PrimaryModule;
  title: string;
  description: string;
  examples: string[];
  icon: typeof Calendar;
}> = [
  {
    value: "appointments",
    title: "Appointments",
    description: "For salons, clinics, spas, and other time-based services.",
    examples: ["Salons", "Barbers", "Nails", "Lashes", "etc."],
    icon: Calendar,
  },
  {
    value: "memberships",
    title: "Memberships",
    description: "For gyms, clubs, studios, and recurring subscriptions.",
    examples: ["Gyms", "Sports Clubs", "Studios", "etc."],
    icon: Users,
  },
  {
    value: "catering_services",
    title: "Catering services",
    description: "For food vendors, meal plans, and company order links.",
    examples: ["Food Vendors", "Caterers", "Meal Prep", "etc."],
    icon: ChefHat,
  },
];

const COUNTRY_DEFAULTS: Record<string, { timezone: string; currency: string; phoneCode?: string }> = {
  Ghana: { timezone: "Africa/Accra", currency: "GHS", phoneCode: "+233" },
  Nigeria: { timezone: "Africa/Lagos", currency: "NGN", phoneCode: "+234" },
  "South Africa": { timezone: "Africa/Johannesburg", currency: "ZAR", phoneCode: "+27" },
  Kenya: { timezone: "Africa/Nairobi", currency: "KES", phoneCode: "+254" },
  Tanzania: { timezone: "Africa/Dar_es_Salaam", currency: "TZS", phoneCode: "+255" },
  Uganda: { timezone: "Africa/Nairobi", currency: "UGX", phoneCode: "+256" },
  Rwanda: { timezone: "Africa/Kigali", currency: "RWF", phoneCode: "+250" },
  Egypt: { timezone: "Africa/Cairo", currency: "EGP", phoneCode: "+20" },
  "Ivory Coast": { timezone: "Africa/Abidjan", currency: "XOF", phoneCode: "+225" },
  "Cote d'Ivoire": { timezone: "Africa/Abidjan", currency: "XOF", phoneCode: "+225" },
  "Côte d'Ivoire": { timezone: "Africa/Abidjan", currency: "XOF", phoneCode: "+225" },
  "United Kingdom": { timezone: "Europe/London", currency: "GBP", phoneCode: "+44" },
  "United States": { timezone: "America/New_York", currency: "USD", phoneCode: "+1" },
  Canada: { timezone: "America/Toronto", currency: "CAD", phoneCode: "+1" },
  Australia: { timezone: "Australia/Sydney", currency: "AUD", phoneCode: "+61" },
  "United Arab Emirates": { timezone: "Asia/Dubai", currency: "AED", phoneCode: "+971" },
  India: { timezone: "Asia/Kolkata", currency: "INR", phoneCode: "+91" },
  Singapore: { timezone: "Asia/Singapore", currency: "SGD", phoneCode: "+65" },
  Germany: { timezone: "Europe/Berlin", currency: "EUR", phoneCode: "+49" },
  France: { timezone: "Europe/Paris", currency: "EUR", phoneCode: "+33" },
  Netherlands: { timezone: "Europe/Amsterdam", currency: "EUR", phoneCode: "+31" },
  Belgium: { timezone: "Europe/Brussels", currency: "EUR", phoneCode: "+32" },
  Spain: { timezone: "Europe/Madrid", currency: "EUR", phoneCode: "+34" },
  Italy: { timezone: "Europe/Rome", currency: "EUR", phoneCode: "+39" },
  Brazil: { timezone: "America/Sao_Paulo", currency: "BRL", phoneCode: "+55" },
};

const COUNTRY_TO_ISO2: Record<string, Country> = {
  Ghana: "GH",
  Nigeria: "NG",
  "South Africa": "ZA",
  Kenya: "KE",
  Tanzania: "TZ",
  Uganda: "UG",
  Rwanda: "RW",
  Egypt: "EG",
  "Ivory Coast": "CI",
  "Cote d'Ivoire": "CI",
  "Côte d'Ivoire": "CI",
  "United Kingdom": "GB",
  "United States": "US",
  Canada: "CA",
  Australia: "AU",
  "United Arab Emirates": "AE",
  India: "IN",
  Singapore: "SG",
  Germany: "DE",
  France: "FR",
  Netherlands: "NL",
  Belgium: "BE",
  Spain: "ES",
  Italy: "IT",
  Brazil: "BR",
};

export default function SignUp() {
  const browserTimezone =
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Accra" : "Africa/Accra";
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [primaryModule, setPrimaryModule] = useState<PrimaryModule>("appointments");
  const [locationDetected, setLocationDetected] = useState(false);
  const [defaultPhoneCountry, setDefaultPhoneCountry] = useState<Country | undefined>("GH");
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNativeIos = isNativeIosApp();

  usePageMeta({
    title: "Sign up – Buukd | Get started free",
    description:
      "Create your Buukd account. Choose Appointments, Memberships, or Catering. Start free — no monthly subscription required for new businesses.",
    path: "/signup",
  });

  const accountForm = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
    },
  });

  const businessForm = useForm<BusinessFormData>({
    resolver: zodResolver(businessSchema),
    defaultValues: {
      businessName: "",
      location: "",
      locationPlaceId: "",
      locationCity: "",
      locationCountry: "",
      locationLat: undefined,
      locationLng: undefined,
      timezone: browserTimezone,
      currency: "USD",
    },
  });

  const businessLocationError =
    businessForm.formState.errors.location?.message || businessForm.formState.errors.locationPlaceId?.message;
  const currentStepCopy = STEP_COPY[currentStep];
  const progressValue = (currentStep / 3) * 100;
  const detectedCountry = businessForm.watch("locationCountry");
  const detectedTimezone = businessForm.watch("timezone");
  const detectedCurrency = businessForm.watch("currency");

  const onModuleContinue = () => {
    setCurrentStep(2);
  };

  const onBusinessSubmit = async () => {
    setCurrentStep(3);
  };

  const onAccountSubmit = async (accountData: AccountFormData) => {
    const businessData = businessForm.getValues();
    if (!businessData.locationPlaceId) {
      setCurrentStep(2);
      businessForm.setError("locationPlaceId", {
        type: "manual",
        message: "Select a location from the suggestions or map",
      });
      return;
    }

    setIsLoading(true);

    const e164Phone = String(accountData.phone || "").trim();
    const parsed = parsePhoneNumberFromString(e164Phone);

    if (!parsed?.isValid() || !parsed.number) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    const { error: signUpError } = await signUp(accountData.email, accountData.password, {
      name: businessData.businessName,
      phone: parsed.number,
      business_name: businessData.businessName,
      business_description: "",
      business_timezone: businessData.timezone,
      business_currency: businessData.currency,
      business_phone: parsed.number,
      location_address: businessData.location,
      location_place_id: businessData.locationPlaceId,
      location_city: businessData.locationCity || "",
      location_country: businessData.locationCountry || "",
      location_latitude: typeof businessData.locationLat === "number" ? String(businessData.locationLat) : "",
      location_longitude: typeof businessData.locationLng === "number" ? String(businessData.locationLng) : "",
      primary_module: primaryModule,
    });

    if (signUpError) {
      setIsLoading(false);
      let message = signUpError.message;
      if (message.includes("already registered")) {
        message = "This email is already registered. Please sign in instead.";
      }
      toast({
        title: "Sign up failed",
        description: message,
        variant: "destructive",
      });
      return;
    }

    const { data: sessionDataBefore } = await supabase.auth.getSession();
    if (!sessionDataBefore?.session?.user?.id) {
      setIsLoading(false);
      navigate("/signup/verify", { state: { email: accountData.email } });
      return;
    }

    setIsLoading(false);
    toast({
      title: "Account created",
      description: "Welcome to Buukd! Your business is ready.",
    });
    navigate("/app");
  };

  if (isNativeIos) {
    return (
      <div className="min-h-screen bg-gradient-signup font-bricolage">
        <main className="public-auth-theme flex min-h-screen items-center justify-center px-4 py-8">
          <div className="glass-card w-full max-w-lg rounded-2xl p-6 text-center sm:p-8">
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Organization access only</h1>
            <p className="mt-3 text-sm text-muted-foreground sm:text-base">
              Buukd accounts in the iOS app are provided by organizations that have already been onboarded.
              Please sign in with the account your organization administrator created for you.
            </p>
            <Button asChild variant="gradient" className="mt-6 w-full sm:w-auto">
              <Link to="/signin">Sign in</Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-signup font-bricolage">
      <main className="public-auth-theme flex flex-1 flex-col items-center px-4 py-4 sm:py-8">
        <div className="w-full max-w-3xl">
          <div className="mb-5 text-center sm:mb-8">
            <h1 className="text-2xl font-bold text-foreground sm:text-4xl">Create your Buukd account</h1>
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">
              Already have an account?{" "}
              <Link to="/signin" className="font-medium text-primary hover:text-primary/90">
                Sign in
              </Link>
            </p>
          </div>

          <div className="glass-card overflow-hidden rounded-2xl">
            <div className="border-b border-border/60 px-5 py-5 sm:px-8 sm:py-6">
              <div className="mb-3 flex items-center justify-between gap-3 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                <span>{`Step ${currentStep} of 3`}</span>
                <span>{currentStepCopy.label}</span>
              </div>
              <Progress
                value={progressValue}
                className="h-1 bg-muted/70 [&>div]:bg-gradient-to-r [&>div]:from-pink-400 [&>div]:to-pink-600"
              />
              <div className="mt-5 space-y-1">
                <h2 className="text-xl font-semibold text-foreground sm:text-2xl">{currentStepCopy.title}</h2>
                <p className="text-sm text-muted-foreground sm:text-base">{currentStepCopy.description}</p>
              </div>
            </div>

            <div className="px-5 py-6 sm:px-8 sm:py-8">
              {currentStep === 1 ? (
                <div className="space-y-6">
                  <div className="grid gap-3 sm:grid-cols-3">
                    {MODULE_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      const isSelected = primaryModule === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setPrimaryModule(option.value)}
                          className={`relative rounded-2xl border p-5 text-left transition-all ${
                            isSelected
                              ? "border-primary bg-primary/10 shadow-sm"
                              : "border-border bg-card hover:border-primary/40 hover:bg-primary/5"
                          }`}
                        >
                          {isSelected && (
                            <span className="absolute right-4 top-4 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                              <Check className="h-3.5 w-3.5" />
                            </span>
                          )}
                          <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <Icon className="h-5 w-5" />
                          </div>
                          <h3 className="text-base font-semibold text-foreground">{option.title}</h3>
                          <p className="mt-2 text-sm text-muted-foreground">{option.description}</p>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {option.examples.map((example) => (
                              <span
                                key={example}
                                className="rounded-full bg-primary/10 px-2.5 py-1 text-xs text-primary"
                              >
                                {example}
                              </span>
                            ))}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex flex-col gap-3 border-t border-border/60 pt-5 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted-foreground">You can add more modules later from your account.</p>
                    <Button variant="gradient" className="w-full sm:w-auto sm:px-8" onClick={onModuleContinue}>
                      Continue
                    </Button>
                  </div>
                </div>
              ) : currentStep === 2 ? (
                <form onSubmit={businessForm.handleSubmit(onBusinessSubmit)} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="businessName" className="text-sm font-medium text-foreground sm:text-base">
                      Business name
                    </Label>
                    <Input
                      id="businessName"
                      type="text"
                      placeholder="Your business name"
                      {...businessForm.register("businessName")}
                      className="h-11 sm:h-12 input-sleek"
                    />
                    {businessForm.formState.errors.businessName && (
                      <p className="text-xs text-destructive sm:text-sm">
                        {businessForm.formState.errors.businessName.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground sm:text-base">Business location</Label>
                    <MapPicker
                      address={businessForm.watch("location") || ""}
                      onAddressChange={(value) => {
                        businessForm.setValue("location", value, { shouldDirty: true });
                        businessForm.setValue("locationPlaceId", "", { shouldDirty: true });
                        businessForm.setValue("locationCity", "");
                        businessForm.setValue("locationCountry", "");
                        businessForm.setValue("locationLat", undefined);
                        businessForm.setValue("locationLng", undefined);
                        businessForm.setValue("timezone", browserTimezone);
                        businessForm.setValue("currency", "USD");
                        setLocationDetected(false);
                      }}
                      lat={businessForm.watch("locationLat") ?? null}
                      lng={businessForm.watch("locationLng") ?? null}
                      onLatLngChange={(pos) => {
                        businessForm.setValue("locationLat", pos?.lat, { shouldDirty: true });
                        businessForm.setValue("locationLng", pos?.lng, { shouldDirty: true });
                      }}
                      onPlaceSelected={(place) => {
                        const defaults = place.country ? COUNTRY_DEFAULTS[place.country] : undefined;
                        const iso2 = place.country ? COUNTRY_TO_ISO2[place.country] : undefined;
                        businessForm.setValue("location", place.formattedAddress, { shouldValidate: true, shouldDirty: true });
                        businessForm.setValue("locationPlaceId", place.placeId || "", { shouldValidate: true, shouldDirty: true });
                        businessForm.setValue("locationCity", place.city || "");
                        businessForm.setValue("locationCountry", place.country || "");
                        businessForm.setValue("locationLat", place.lat, { shouldDirty: true });
                        businessForm.setValue("locationLng", place.lng, { shouldDirty: true });
                        businessForm.setValue("timezone", defaults?.timezone || browserTimezone);
                        businessForm.setValue("currency", defaults?.currency || "USD");
                        if (iso2) setDefaultPhoneCountry(iso2);
                        setLocationDetected(true);
                      }}
                      disabled={isLoading}
                    />
                    {businessLocationError && <p className="text-xs text-destructive sm:text-sm">{businessLocationError}</p>}
                    <p className="text-xs text-muted-foreground">
                      Choose a real location from the suggestions or map. We will use it to set your timezone and currency.
                    </p>
                  </div>

                  {locationDetected && (
                    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
                          <Wand2 className="h-4 w-4" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-foreground">Timezone and currency detected</p>
                          <p className="text-sm text-muted-foreground">
                            {[detectedCountry, detectedTimezone, detectedCurrency].filter(Boolean).join(" · ")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            You can change these later in your settings.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col-reverse gap-3 border-t border-border/60 pt-5 sm:flex-row sm:items-center sm:justify-between">
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(1)} className="w-full sm:w-auto">
                      Back
                    </Button>
                    <Button type="submit" variant="gradient" className="w-full sm:w-auto sm:px-8">
                      Continue
                    </Button>
                  </div>
                </form>
              ) : (
                <form onSubmit={accountForm.handleSubmit(onAccountSubmit)} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-foreground sm:text-base">
                      Email address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      {...accountForm.register("email")}
                      className="h-11 sm:h-12 input-sleek"
                    />
                    {accountForm.formState.errors.email && (
                      <p className="text-xs text-destructive sm:text-sm">{accountForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-foreground sm:text-base">
                      Phone number
                    </Label>
                    <Controller
                      control={accountForm.control}
                      name="phone"
                      render={({ field }) => (
                        <div className="rounded-md border border-input bg-background px-3 py-2 sm:py-2.5">
                          <PhoneInput
                            international
                            defaultCountry={defaultPhoneCountry}
                            value={field.value as any}
                            onChange={field.onChange}
                            placeholder="+233 24 141 3901"
                            numberInputProps={{
                              className: "w-full bg-transparent outline-none text-sm sm:text-base",
                              id: "phone",
                              name: "phone",
                              disabled: isLoading,
                              autoComplete: "tel",
                            }}
                          />
                        </div>
                      )}
                    />
                    {accountForm.formState.errors.phone && (
                      <p className="text-xs text-destructive sm:text-sm">{accountForm.formState.errors.phone.message}</p>
                    )}
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-sm font-medium text-foreground sm:text-base">
                        Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Create a password"
                          {...accountForm.register("password")}
                          className="h-11 pr-10 sm:h-12 input-sleek"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((value) => !value)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {accountForm.formState.errors.password && (
                        <p className="text-xs text-destructive sm:text-sm">{accountForm.formState.errors.password.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground sm:text-base">
                        Confirm password
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Repeat your password"
                          {...accountForm.register("confirmPassword")}
                          className="h-11 pr-10 sm:h-12 input-sleek"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((value) => !value)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {accountForm.formState.errors.confirmPassword && (
                        <p className="text-xs text-destructive sm:text-sm">
                          {accountForm.formState.errors.confirmPassword.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Password must be at least 6 characters and include at least one number.
                  </p>

                  <div className="flex flex-col-reverse gap-3 border-t border-border/60 pt-5 sm:flex-row sm:items-center sm:justify-between">
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(2)} className="w-full sm:w-auto">
                      Back
                    </Button>
                    <Button type="submit" variant="gradient" className="w-full sm:w-auto sm:px-8" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        "Create account"
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
